import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, GripVertical, X } from 'lucide-react';
import { getSocialApiBaseUrl } from '../../../utils/socialApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymModalPanel,
  gymSocialBtnPrimary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';
import { contentTypeBadgeClass, contentTypeI18nKey } from './socialContentTypes';
import GymSocialMediaThumb from './GymSocialMediaThumb';
import SocialTargetBadges from './SocialTargetBadges';
import { mediaItemUrl } from './socialMediaUtils';

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  scheduled: 'bg-amber-500',
  publishing: 'bg-blue-400',
  published: 'bg-green-600',
  failed: 'bg-red-500',
  cancelled: 'bg-gray-400',
};

const STATUS_FILTER_OPTIONS = ['', 'scheduled', 'published', 'failed', 'draft', 'publishing'];

const DRAG_MIME = 'application/x-gym-social-post';

function pad(n) {
  return String(n).padStart(2, '0');
}

function postFirstMedia(ev) {
  return Array.isArray(ev.media) ? ev.media[0] : null;
}

/** Sposta la data mantenendo l'orario (o 10:00). */
function shiftScheduledToDay(scheduledAt, dayKey) {
  const raw = String(scheduledAt || '');
  const timeMatch = raw.match(/(\d{2}):(\d{2})/);
  const time = timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : '10:00';
  return `${dayKey}T${time}`;
}

function canDragPost(ev) {
  return ev.status === 'scheduled' || ev.status === 'failed';
}

function CalendarEventChip({ ev, t, onDragStart }) {
  const firstMedia = postFirstMedia(ev);
  const hasThumb = Boolean(mediaItemUrl(firstMedia));
  const contentType = ev.content_type || 'post';
  const draggable = canDragPost(ev);

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={`flex items-center gap-1 min-w-0 rounded px-0.5 ${
        draggable ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      title={
        draggable
          ? t('gym.social.calendar.dragHint')
          : ev.caption || t(contentTypeI18nKey(contentType))
      }
      onClick={(e) => e.stopPropagation()}
    >
      {draggable && (
        <GripVertical className="h-3 w-3 shrink-0 opacity-50 text-gray-500 dark:text-white/50" aria-hidden />
      )}
      {hasThumb ? (
        <GymSocialMediaThumb
          item={firstMedia}
          className="h-7 w-7 shrink-0 rounded overflow-hidden border border-white/30 bg-black"
        />
      ) : (
        <span className={`h-7 w-7 shrink-0 rounded ${STATUS_COLORS[ev.status] || 'bg-gray-400'}`} />
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-0.5">
          <span
            className={`text-[8px] font-semibold uppercase tracking-wide px-1 py-px rounded leading-none ${contentTypeBadgeClass(contentType)}`}
          >
            {t(contentTypeI18nKey(contentType))}
          </span>
          <SocialTargetBadges targets={ev.targets} />
        </div>
        <span
          className={`truncate text-[10px] text-white px-1 py-0.5 rounded ${STATUS_COLORS[ev.status] || 'bg-gray-400'}`}
        >
          {ev.caption?.slice(0, 12) || '-'}
        </span>
      </div>
    </div>
  );
}

export default function GymSocialCalendar({ api, onCreateForDay, onChanged, refreshKey = 0 }) {
  const { t } = useTranslation();
  const socialApi = getSocialApiBaseUrl();
  const [viewDate, setViewDate] = useState(() => new Date());
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dropDay, setDropDay] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);

  const month = viewDate.getMonth() + 1;
  const year = viewDate.getFullYear();

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const res = await api.get('gym_social_posts.php?action=calendar', {
        baseURL: socialApi,
        params,
      });
      setDays(res.data.days || {});
    } catch {
      setDays({});
    } finally {
      setLoading(false);
    }
  }, [api, socialApi, month, year, statusFilter]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar, refreshKey]);

  const reschedulePost = async (postId, scheduledAt, fromDayKey, toDayKey) => {
    setReschedulingId(postId);
    try {
      await api.post(
        'gym_social_posts.php?action=reschedule',
        { id: postId, scheduled_at: scheduledAt },
        { baseURL: socialApi }
      );
      toast.success(t('gym.social.calendar.rescheduled', { from: fromDayKey, to: toDayKey }));
      await fetchCalendar();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.calendar.rescheduleError'));
    } finally {
      setReschedulingId(null);
      setDropDay(null);
    }
  };

  const handleDropOnDay = (e, toDayKey) => {
    e.preventDefault();
    e.stopPropagation();
    setDropDay(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData(DRAG_MIME));
    } catch {
      return;
    }
    if (!payload?.id || !payload?.scheduled_at) {
      return;
    }
    const fromDayKey = String(payload.scheduled_at).slice(0, 10);
    if (fromDayKey === toDayKey) {
      return;
    }
    const newAt = shiftScheduledToDay(payload.scheduled_at, toDayKey);
    reschedulePost(payload.id, newAt, fromDayKey, toDayKey);
  };

  const firstOfMonth = new Date(year, month - 1, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(d);
  }

  const dayKey = (d) => `${year}-${pad(month)}-${pad(d)}`;

  const prevMonth = () => setViewDate(new Date(year, month - 2, 1));
  const nextMonth = () => setViewDate(new Date(year, month, 1));

  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className={`${gymSocialCard} p-6 space-y-4 w-full max-w-full`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>
          {t('gym.social.calendar.title')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={prevMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeft className={`h-5 w-5 ${gymSocialTitleText}`} />
          </button>
          <span className={`text-sm font-medium min-w-[160px] text-center capitalize ${gymSocialTitleText}`}>
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronRight className={`h-5 w-5 ${gymSocialTitleText}`} />
          </button>
        </div>
      </div>

      <p className={`text-xs ${gymSocialMutedText}`}>{t('gym.social.calendar.dragHintGlobal')}</p>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs font-medium ${gymSocialMutedText}`}>
          {t('gym.social.calendar.filterStatus')}
        </span>
        {STATUS_FILTER_OPTIONS.map((st) => (
          <button
            key={st || 'all'}
            type="button"
            onClick={() => setStatusFilter(st)}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              statusFilter === st
                ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-600 dark:border-indigo-500'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-white/80 dark:hover:bg-gray-700'
            }`}
          >
            {st === '' ? t('gym.social.calendar.filterAll') : t(`gym.social.posts.status.${st}`)}
          </button>
        ))}
      </div>

      <div className={`flex flex-wrap gap-x-4 gap-y-2 text-xs mb-2 ${gymSocialMutedText}`}>
        <span className="font-medium text-gray-700 dark:text-white/80">{t('gym.social.calendar.legendStatus')}</span>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <span key={status} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${color}`} />
            {t(`gym.social.posts.status.${status}`)}
          </span>
        ))}
      </div>
      <div className={`flex flex-wrap gap-2 text-xs mb-2 ${gymSocialMutedText}`}>
        <span className="font-medium text-gray-700 dark:text-white/80">{t('gym.social.calendar.legendType')}</span>
        {['post', 'story', 'reel'].map((type) => (
          <span
            key={type}
            className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${contentTypeBadgeClass(type)}`}
          >
            {t(contentTypeI18nKey(type))}
          </span>
        ))}
        <span className="font-medium text-gray-700 dark:text-white/80 ml-2">
          {t('gym.social.calendar.legendTargets')}
        </span>
        <SocialTargetBadges targets={['facebook', 'instagram']} />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-7 gap-1 text-center text-xs font-medium mb-1 ${gymSocialMutedText}`}>
            {['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'].map((d) => (
              <div key={d}>{t(`gym.social.calendar.weekdays.${d}`)}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {cells.map((d, idx) => {
              if (d === null) {
                return <div key={`e-${idx}`} className="min-h-[88px] sm:min-h-[104px]" />;
              }
              const key = dayKey(d);
              const events = days[key] || [];
              const isToday =
                d === new Date().getDate() &&
                month === new Date().getMonth() + 1 &&
                year === new Date().getFullYear();
              const isDropTarget = dropDay === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDay({ key, d, events })}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropDay(key);
                  }}
                  onDragLeave={() => setDropDay((prev) => (prev === key ? null : prev))}
                  onDrop={(e) => handleDropOnDay(e, key)}
                  className={`min-h-[88px] sm:min-h-[104px] rounded-lg border p-1.5 text-left text-sm transition bg-white hover:border-indigo-400 dark:bg-gray-800/40 dark:hover:border-indigo-500 ${
                    isDropTarget
                      ? 'ring-2 ring-indigo-400 ring-offset-1 dark:ring-indigo-500'
                      : ''
                  } ${
                    isToday
                      ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-600/25'
                      : 'border-gray-200 dark:border-white/15'
                  }`}
                >
                  <span className={`font-medium ${gymSocialTitleText}`}>{d}</span>
                  <div className="mt-1 space-y-1">
                    {events.slice(0, 3).map((ev) => (
                      <CalendarEventChip
                        key={ev.id}
                        ev={ev}
                        t={t}
                        onDragStart={(e) => {
                          if (!canDragPost(ev)) {
                            e.preventDefault();
                            return;
                          }
                          e.stopPropagation();
                          e.dataTransfer.setData(
                            DRAG_MIME,
                            JSON.stringify({ id: ev.id, scheduled_at: ev.scheduled_at })
                          );
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                      />
                    ))}
                    {events.length > 3 && (
                      <span className={`text-[10px] ${gymSocialMutedText}`}>+{events.length - 3}</span>
                    )}
                    {reschedulingId && events.some((ev) => ev.id === reschedulingId) && (
                      <span className={`text-[10px] ${gymSocialMutedText}`}>
                        {t('gym.social.calendar.rescheduling')}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {selectedDay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className={`${gymModalPanel} max-w-md w-full p-6 max-h-[80vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`font-semibold ${gymSocialTitleText}`}>
                {t('gym.social.calendar.dayTitle', { date: selectedDay.key })}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                className="p-1 rounded-lg hover:bg-white/10"
              >
                <X className={`h-5 w-5 ${gymSocialTitleText}`} />
              </button>
            </div>
            {selectedDay.events.length === 0 ? (
              <p className={`text-sm mb-4 ${gymSocialMutedText}`}>{t('gym.social.calendar.noPosts')}</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {selectedDay.events.map((ev) => {
                  const firstMedia = postFirstMedia(ev);
                  const hasThumb = Boolean(mediaItemUrl(firstMedia));
                  const contentType = ev.content_type || 'post';
                  const mediaCount = Array.isArray(ev.media) ? ev.media.length : 0;
                  return (
                    <li
                      key={ev.id}
                      className="text-sm border border-gray-200 bg-gray-50 rounded-lg p-3 dark:border-white/15 dark:bg-gray-800/50"
                    >
                      <div className="flex gap-3">
                        {hasThumb && (
                          <GymSocialMediaThumb
                            item={firstMedia}
                            className="h-16 w-16 shrink-0 rounded-lg overflow-hidden border border-gray-200 dark:border-white/15 bg-black"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${contentTypeBadgeClass(contentType)}`}
                            >
                              {t(contentTypeI18nKey(contentType))}
                            </span>
                            <SocialTargetBadges targets={ev.targets} />
                            <span
                              className={`inline-block text-[10px] text-white px-1.5 py-0.5 rounded ${STATUS_COLORS[ev.status]}`}
                            >
                              {t(`gym.social.posts.status.${ev.status}`)}
                            </span>
                            {contentType === 'post' && mediaCount > 1 && (
                              <span className={`text-[10px] ${gymSocialMutedText}`}>
                                {t('gym.social.posts.carouselBadge', { count: mediaCount })}
                              </span>
                            )}
                          </div>
                          <p className={gymSocialTitleText}>{ev.caption || '-'}</p>
                          {ev.scheduled_at && (
                            <p className={`text-xs mt-1 ${gymSocialMutedText}`}>{ev.scheduled_at}</p>
                          )}
                          {canDragPost(ev) && (
                            <p className={`text-xs mt-1 ${gymSocialMutedText}`}>
                              {t('gym.social.calendar.dragHint')}
                            </p>
                          )}
                          {ev.publish_error && (
                            <p className="text-xs text-red-500 dark:text-red-300 mt-1">{ev.publish_error}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <button
              type="button"
              onClick={() => {
                const dt = `${selectedDay.key}T10:00`;
                onCreateForDay?.(dt);
                setSelectedDay(null);
              }}
              className={`${gymSocialBtnPrimary} w-full justify-center`}
            >
              {t('gym.social.calendar.addPost')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
